import {reactive} from "vue";
import SelectedDatesModel from "./SelectedDatesModel.js";
import CalendarDateModel from "./CalendarDateModel.js";
import CalendarConfigModel from "./CalendarConfigModel.js";
import {convertObjectToUrlSearchParams, convertQueryStringToObject} from "../helpers.js";

export default class MultiSelectedDatesModel
{

    /**
     * @type {Map<SelectedDatesModel>}
     */
    selectedDatesModels = reactive(new Map());

    /**
     * @param calendarConfigModel {CalendarConfigModel}
     */
    constructor(calendarConfigModel)
    {
        this.calendarConfigModel = calendarConfigModel;
    }

    /**
     * @param selectedDatesModel {SelectedDatesModel}
     */
    addSelectedDatesModel(selectedDatesModel)
    {
        this.selectedDatesModels.set(selectedDatesModel.getUUID(), selectedDatesModel);
        this.handleEmitEventWhenNextCalendarToBeSelected();
    }



    handleEmitEventWhenNextCalendarToBeSelected()
    {
        let nextHotelIDToScrollTop = null;

        this.selectedDatesModels.forEach(selectedDatesModel => {
            /**
             * @type selectedDatesModel {SelectedDatesModel}
             */
            if (selectedDatesModel.isEmpty() && ! nextHotelIDToScrollTop) {
                nextHotelIDToScrollTop = selectedDatesModel.getHotelID();
            }
        });

        if (this.hasSelectedDateForOneCalendar() && nextHotelIDToScrollTop) {
            window.dispatchEvent(new CustomEvent('calendarScrollTo', {
                detail: {
                    hotelID: nextHotelIDToScrollTop
                }
            }));
        }
    }


    getUrl()
    {
        let url = new URL(
            this.calendarConfigModel.availabilityUrl
        );
        let queryParameters = convertQueryStringToObject(decodeURI(url.search));

        this.selectedDatesModels.forEach(selectedDatesModel => {
            /**
             * @type selectedDatesModel {SelectedDatesModel}
             */
            if (selectedDatesModel.hasSelectedDates()) {
                queryParameters.hotels ??= [];
                queryParameters.arrivals ??= [];
                queryParameters.departures ??= [];
                queryParameters.hotels.push(selectedDatesModel.getHotelID());
                queryParameters.arrivals.push(selectedDatesModel.getArrivalDate());
                queryParameters.departures.push(selectedDatesModel.getDepartureDate());
            }
        });

        return url.origin + url.pathname + '?' + convertObjectToUrlSearchParams(queryParameters);
    }

    /**
     * @returns {boolean}
     */
    hasSelectedDatesForAllCalendars()
    {
        let hasSelectedDatesForAllCalendars = true;

        for (const selectedDatesModel of this.selectedDatesModels.values())
        {
            if (! selectedDatesModel.hasSelectedDates()) {
                hasSelectedDatesForAllCalendars = false;
                break;
            }
        }

        return hasSelectedDatesForAllCalendars;
    }

    /**
     * @returns {boolean}
     */
    hasSelectedDateForOneCalendar()
    {
        let hasSelectedDatesForOneCalendar = false;

        for (const selectedDatesModel of this.selectedDatesModels.values())
        {
            if (selectedDatesModel.hasSelectedDates()) {
                hasSelectedDatesForOneCalendar = true;
                break;
            }
        }

        return hasSelectedDatesForOneCalendar;
    }

    getSelectedDateModels()
    {
        return Array.from(this.selectedDatesModels.values());
    }

    /**
     * @param currentCalendarID {string}
     * @returns {SelectedDatesModel[]}
     * @private
     */
    _getSelectedDateModels(currentCalendarID)
    {
        let selectedDatesModels = [];

        for (const [index, selectedDatesModel] of this.selectedDatesModels.entries()) {

            if (selectedDatesModel.getUUID() !== currentCalendarID) {
                selectedDatesModels.push(selectedDatesModel);
            }
        }

        return selectedDatesModels;
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isCalendarDateTransferDate(calendarDateModel)
    {
        let allDates = [];
        this.selectedDatesModels.forEach(selectedDatesModel => {
            if (selectedDatesModel.hasSelectedDates()) {
                allDates = allDates.concat(selectedDatesModel.getSelectedDates());
            }
        });
        allDates.sort();

        if (allDates.length < 2) {
            return false;
        }

        const betweenDates = allDates.slice(1, -1);
        return calendarDateModel.isSelectedArrival() && betweenDates.includes(calendarDateModel.getDate());
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isCalendarDateSelectedInAnotherCalendar(calendarDateModel)
    {
        let isSelectedInAnotherCalendar = false;

        for (const selectedDatesModel of this._getSelectedDateModels(calendarDateModel.getUUID())) {
            if (selectedDatesModel.hasDate(calendarDateModel.getDate())) {
                isSelectedInAnotherCalendar = true;
                break;
            }
        }

        return isSelectedInAnotherCalendar;
    }

}
